import {
  type CpAnalysisDay,
  type CpLeg,
  type CpSeaTime,
  type CpVerdict,
  cpAllowance,
  cpVerdict,
  DOUGLAS_WIND_SEA_M,
  performanceReference,
} from '@felix/domain';
import {
  Button,
  Modal,
  Pagination,
  RingProgress,
  Select,
  Skeleton,
  Table,
  Textarea,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCalendarTime,
  IconChecklist,
  IconFileDescription,
  IconInfoCircle,
  IconRoute,
  IconScale,
  IconSortAscending,
  IconSortDescending,
  IconSunWind,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import cpClasses from './CharterPartyScreens.module.css';
import { ScrollTable } from './fields.js';
import shared from './NoonReportScreen.module.css';
import { useNumber } from './numbers.js';
import { cpCompliancePct } from './overview.js';
import { CompareTable, CpKpis, Facts } from './PerformanceScreens.js';
import classes from './PerformanceScreens.module.css';
import type { VoyageSource } from './source.js';
import type {
  CharterPartyAnalysisData,
  CharterPartyEventView,
  CharterPartyItem,
  CharterPartyRegisterData,
} from './types.js';
import { ReferenceSummary } from './VoyageReference.js';

type Message = { tone: 'success' | 'danger'; text: string } | null;

export type CpAnalysisLoad =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | {
      kind: 'ready';
      vessels: PersonnelVessel[];
      vessel: PersonnelVessel | null;
      register: CharterPartyRegisterData;
      cpId: string | null;
      data: CharterPartyAnalysisData | null;
    };

/** Ofisin karar hedefi: gün (Noon) ya da bacak (COSP), eylemle. */
export interface CpDecisionTarget {
  kind: 'day' | 'leg';
  id: string;
  action: 'exclude' | 'include';
  at: string;
}

const VERDICT_TONE: Record<CpVerdict['status'], StatusTone> = {
  within: 'success',
  outside: 'warning',
  partial: 'info',
  unknown: 'neutral',
};

const utc = (at: string | null | undefined) => (at ? `${formatDateTime(at, 0)} UTC` : '—');

function useCp() {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.cpAnalysis.${key}`, options);
  const c = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.charterParty.${key}`, options);
  return { t, k, c };
}

export interface CharterPartyPerformanceViewProps {
  load: CpAnalysisLoad;
  scope: 'web' | 'ship';
  /** Ofiste `voyage.edit` ve yazma kaynağı var. */
  canDecide: boolean;
  message?: Message;
  onVessel: (id: string) => void;
  onCp: (id: string) => void;
  onRetry: () => void;
  onOpenRegister: () => void;
  onDecide: (target: CpDecisionTarget) => void;
  onReview: () => void;
}

/**
 * Charter Party Performance (ADR 0084 Ek; ortağın `sefer-4.jpeg`): bir CP'nin gerçekleşen ve
 * sözleşmedeki performansı. Önce sonuç (iyi hava günleri) ve etiketi ("Indicative" / "Reviewed"),
 * sonra şartlar ve gerçekleşen ile sözleşme, seyir süresi ve hariç dönemler, bacak analizi, iyi
 * hava günleri ve ofis incelemesi. Gemide salt okunur.
 */
export function CharterPartyPerformanceView({
  load,
  scope,
  canDecide,
  message = null,
  onVessel,
  onCp,
  onRetry,
  onOpenRegister,
  onDecide,
  onReview,
}: CharterPartyPerformanceViewProps) {
  const { t, k, c } = useCp();
  const ready = load.kind === 'ready' ? load : null;
  const data = ready?.data ?? null;
  const label = data?.result.label ?? null;

  return (
    <div className={cpClasses.layout}>
      <PageHeader
        title={c('title')}
        description={k('description')}
        icon={<IconScale size={28} stroke={1.6} />}
        {...(label
          ? {
              actions: (
                <StatusBadge
                  tone={label === 'reviewed' ? 'success' : 'info'}
                  withIcon
                  outlined={label !== 'reviewed'}
                >
                  {k(`label.${label}`)}
                </StatusBadge>
              ),
            }
          : {})}
      />

      <Panel flush>
        <form
          className={cpClasses.filters}
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 320px))' }}
          aria-label={k('charterParty')}
          onSubmit={(event) => event.preventDefault()}
        >
          {scope === 'web' ? (
            <Select
              label={t('voyage.charterParties.vessel')}
              aria-label={t('shell.selectVessel')}
              data={(ready?.vessels ?? []).map((v) => ({ value: v.id, label: v.name }))}
              value={ready?.vessel?.id ?? null}
              allowDeselect={false}
              onChange={(value) => value && onVessel(value)}
            />
          ) : null}
          <Select
            label={k('charterParty')}
            data={(ready?.register.items ?? []).map((item) => ({
              value: item.id,
              label: [item.cpNo ?? '—', item.charterer].filter(Boolean).join(' · '),
            }))}
            value={ready?.cpId ?? null}
            allowDeselect={false}
            disabled={(ready?.register.items.length ?? 0) === 0}
            onChange={(value) => value && onCp(value)}
          />
        </form>
      </Panel>

      {message ? (
        <p
          className={cpClasses.message}
          data-tone={message.tone}
          role={message.tone === 'danger' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      ) : null}

      {load.kind === 'loading' ? (
        <>
          <Skeleton height={150} radius="md" />
          <div className={classes.cpGrid}>
            <Skeleton height={320} radius="md" />
            <Skeleton height={320} radius="md" />
          </div>
          <Skeleton height={360} radius="md" />
        </>
      ) : load.kind === 'error' ? (
        <Panel>
          <EmptyState
            tone="danger"
            title={k('error')}
            action={
              <Button variant="default" onClick={onRetry}>
                {t('voyage.charterParties.retry')}
              </Button>
            }
          />
        </Panel>
      ) : !data ? (
        <>
          <Panel>
            <EmptyState
              icon={<IconFileDescription size={22} stroke={1.8} />}
              title={k('noCpTitle')}
              description={k('noCp')}
              action={
                <Button variant="default" onClick={onOpenRegister}>
                  {k('openRegister')}
                </Button>
              }
            />
          </Panel>
          {/* CP yoksa analiz Owner's reference'ı kullanır (ADR 0084 Ek 2); sonuç Indicative */}
          {ready && (ready.register.items.length ?? 0) === 0 ? (
            <Panel title={t('voyage.cpReference.registerDefault')}>
              <p className={cpClasses.hint}>{t('voyage.cpReference.registerDefaultHint')}</p>
              <ReferenceSummary
                reference={performanceReference({
                  charterParty: null,
                  voyageOwner: null,
                  vesselOwner: ready.register.ownerReference ?? null,
                })}
                charterParty={null}
                basis={null}
              />
            </Panel>
          ) : null}
        </>
      ) : (
        <AnalysisBody data={data} canDecide={canDecide} onDecide={onDecide} onReview={onReview} />
      )}
    </div>
  );
}

function AnalysisBody({
  data,
  canDecide,
  onDecide,
  onReview,
}: {
  data: CharterPartyAnalysisData;
  canDecide: boolean;
  onDecide: (target: CpDecisionTarget) => void;
  onReview: () => void;
}) {
  const { t, k, c } = useCp();
  const n = useNumber();
  const one = { digits: 1, fixed: true } as const;
  const { charterParty: cp, analysis, result } = data;
  const terms = cp.terms;
  const performance = analysis.performance;
  const evaluation = analysis.evaluation;
  const goodWeather = evaluation?.goodWeather ?? null;
  const plannedSpeed = analysis.plannedSpeedKn;
  const pct = cpCompliancePct(goodWeather?.avgSpeedKn ?? null, plannedSpeed);
  const goodVerdict = cpVerdict(goodWeather?.comparison ?? null);
  const wholeVerdict = cpVerdict(evaluation?.whole.comparison ?? null);
  const allowance = cpAllowance(goodWeather?.comparison ?? null, terms);
  const allowanceState = !allowance
    ? 'null'
    : allowance.speed === false || allowance.consumption === false
      ? 'false'
      : allowance.speed === null && allowance.consumption === null
        ? 'null'
        : 'true';
  const pair = (a: number | null, b: number | null, unit: string) =>
    a === null && b === null ? '—' : `${n(a, one)} / ${n.unit(b, unit, one)}`;
  const limits = [
    terms.maxBeaufort === null ? null : c('beaufortLimit', { value: terms.maxBeaufort }),
    terms.maxWaveHeightM === null ? null : c('waveLimit', { value: n(terms.maxWaveHeightM, one) }),
    terms.maxDouglas === null ? null : k('douglasLimit', { value: terms.maxDouglas }),
    terms.maxSwellM === null ? null : k('swellLimit', { value: n(terms.maxSwellM, one) }),
  ].filter(Boolean);

  return (
    <>
      <div
        className={cpClasses.notice}
        data-tone={result.outdated ? 'warning' : undefined}
        role="note"
      >
        {result.outdated ? (
          <IconAlertTriangle size={16} aria-hidden />
        ) : (
          <IconInfoCircle size={16} aria-hidden />
        )}
        <span>
          <strong>{k(`label.${result.label}`)}</strong>
          {result.label === 'reviewed' && result.review
            ? ` · ${k('reviewedBy', {
                name: result.review.createdByName ?? '—',
                at: formatDateTime(result.review.createdAt, 0),
              })}`
            : null}
          <br />
          {result.outdated && result.review
            ? k('outdated', { at: formatDateTime(result.review.createdAt, 0) })
            : k('claims')}
          {cp.period.startBasis === 'planned' ? (
            <>
              <br />
              {k('plannedBasis')}
            </>
          ) : null}
        </span>
      </div>

      <Panel
        title={c('status')}
        icon={<IconScale size={20} />}
        divider
        extra={
          performance ? (
            <StatusBadge tone={VERDICT_TONE[goodVerdict.status]} withIcon>
              {c(`goodVerdict.${goodVerdict.status}`)}
            </StatusBadge>
          ) : null
        }
      >
        {performance ? (
          <div className={classes.statusStrip}>
            <div className={classes.ring}>
              <RingProgress
                size={104}
                thickness={9}
                roundCaps
                aria-label={c('compliance')}
                rootColor="var(--felix-page-bg)"
                sections={[
                  {
                    value: pct ?? 0,
                    color:
                      pct !== null && pct >= 100
                        ? 'var(--felix-success-fg)'
                        : 'var(--felix-warning-500)',
                  },
                ]}
                label={
                  <strong className={classes.ringLabel}>{pct === null ? '—' : `${pct}%`}</strong>
                }
              />
              <span className={classes.ringCaption}>{c('compliance')}</span>
            </div>
            <CpKpis comparison={goodWeather?.comparison ?? null} plannedSpeed={plannedSpeed} />
            <div className={classes.stripNote}>
              <div
                className={shared.notice}
                data-tone={
                  goodVerdict.status === 'within'
                    ? 'success'
                    : goodVerdict.status === 'outside'
                      ? 'warning'
                      : 'info'
                }
              >
                <IconInfoCircle size={16} aria-hidden />
                <span>
                  {c(`verdictText.${goodVerdict.status}`)}
                  <br />
                  <small>
                    {goodWeather && goodWeather.days > 0
                      ? c('goodBasis', {
                          days: goodWeather.days,
                          hours: n(goodWeather.hours, one),
                          distance: n(goodWeather.distanceNm, one),
                        })
                      : c('noGoodDays')}
                  </small>
                </span>
              </div>
            </div>
          </div>
        ) : (
          <EmptyState
            icon={<IconScale size={22} stroke={1.8} />}
            title={c('noWindowTitle')}
            description={k('noLegs')}
          />
        )}
      </Panel>

      <div className={classes.cpGrid}>
        <div className={classes.factsColumn}>
          <Panel title={k('terms')} icon={<IconFileDescription size={20} />} divider>
            <Facts
              rows={[
                [t('voyage.noon.voyageTab.charterer'), cp.charterer ?? '—'],
                [t('voyage.noon.voyageTab.charterParty'), cp.cpNo ?? '—'],
                [
                  k('period'),
                  k('periodValue', {
                    from: utc(cp.period.start),
                    to: cp.period.end ? utc(cp.period.end) : t('voyage.charterParties.open'),
                  }),
                ],
                [k('speed'), pair(terms.speedLadenKn, terms.speedBallastKn, 'kn')],
                [
                  k('consumption'),
                  pair(terms.consumptionLadenMt, terms.consumptionBallastMt, 'MT/day'),
                ],
                [k('weather'), limits.length === 0 ? '—' : limits.join(', ')],
                [
                  k('about'),
                  terms.speedToleranceKn === null && terms.consumptionTolerancePct === null
                    ? '—'
                    : k('aboutValue', {
                        speed: n(terms.speedToleranceKn ?? 0, one),
                        consumption: n(terms.consumptionTolerancePct ?? 0, one),
                      }),
                ],
                [k('current'), terms.currentClause ?? '—'],
                [
                  k('minDay'),
                  terms.minDayHours === null ? '—' : n.unit(terms.minDayHours, 'h', one),
                ],
                [
                  k('exclusions'),
                  terms.exclusions.length === 0
                    ? '—'
                    : terms.exclusions
                        .map((e) => t(`voyage.charterParties.form.exclusion.${e}`))
                        .join(', '),
                ],
                [c('fuel'), `${terms.mainFuel ?? '—'} / ${terms.auxFuel ?? '—'}`],
              ]}
            />
          </Panel>
        </div>
        <Panel
          title={k('comparison')}
          icon={<IconScale size={20} />}
          divider
          extra={
            performance ? (
              <StatusBadge tone={VERDICT_TONE[wholeVerdict.status]} withIcon>
                {c(`wholeVerdict.${wholeVerdict.status}`)}
              </StatusBadge>
            ) : null
          }
        >
          {performance ? (
            <div className={classes.periods}>
              <div className={classes.period}>
                <h3>{c('goodWeatherDays')}</h3>
                <CompareTable
                  label={c('goodWeatherTable')}
                  values={goodWeather}
                  plannedSpeed={plannedSpeed}
                />
              </div>
              <div className={classes.period}>
                <h3>{c('wholePeriod')}</h3>
                <CompareTable
                  label={c('wholeTable')}
                  values={evaluation?.whole ?? null}
                  plannedSpeed={plannedSpeed}
                />
              </div>
              <div className={classes.period}>
                <h3>{k('seaTime')}</h3>
                <SeaTimeTable
                  goodWeather={analysis.seaTime.goodWeather}
                  whole={analysis.seaTime.whole}
                />
              </div>
              {allowance ? (
                <p className={classes.ruleNote}>
                  {k('allowance', {
                    speed: n(allowance.minSpeedKn, one),
                    consumption: n(allowance.maxConsumptionMt, one),
                  })}
                  {` (${k(`allowanceVerdict.${allowanceState}`)})`}
                </p>
              ) : null}
              <p className={classes.ruleNote}>
                {c('rule')} {k('strictNote')}
              </p>
            </div>
          ) : (
            <EmptyState
              icon={<IconScale size={22} stroke={1.8} />}
              title={c('noWindowTitle')}
              description={k('noLegs')}
            />
          )}
        </Panel>
      </div>

      <Panel title={k('excluded')} icon={<IconCalendarTime size={20} />} divider>
        {performance ? (
          <Facts
            rows={[
              [c('cosp'), utc(performance.start)],
              [c('eosp'), performance.end ? utc(performance.end) : c('open')],
              [c('windowHours'), n.unit(performance.windowHours, 'h', one)],
              [c('excludedShort'), n.unit(performance.excludedHours, 'h', one)],
              [c('performanceHours'), n.unit(performance.hours, 'h', one)],
              [c('distance'), n.unit(performance.distanceNm, 'NM', one)],
              [
                c('loadCondition'),
                performance.loadCondition
                  ? t(`voyage.event.options.loadConditions.${performance.loadCondition}`)
                  : '—',
              ],
            ]}
          />
        ) : null}
        {analysis.excludedPeriods.length === 0 ? (
          <p className={cpClasses.hint}>{k('noExcluded')}</p>
        ) : (
          <ScrollTable label={k('excludedTable')}>
            <table className={cpClasses.table}>
              <thead>
                <tr>
                  <th scope="col">{k('kind')}</th>
                  <th scope="col">{k('from')}</th>
                  <th scope="col">{k('to')}</th>
                  <th scope="col" data-numeric>
                    {k('hours')}
                  </th>
                  <th scope="col">{k('reason')}</th>
                </tr>
              </thead>
              <tbody>
                {analysis.excludedPeriods.map((period) => (
                  <tr key={`${period.kind}-${period.start}`}>
                    <td>
                      {k(`kinds.${period.kind}`)}
                      {period.name ? <span className={cpClasses.sub}>{period.name}</span> : null}
                    </td>
                    <td>{formatDateTime(period.start, 0)}</td>
                    <td>{period.end ? formatDateTime(period.end, 0) : '—'}</td>
                    <td data-numeric>{n(period.hours, one)}</td>
                    <td>{period.reason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollTable>
        )}
      </Panel>

      <LegTable
        legs={analysis.legs}
        canDecide={canDecide}
        onDecide={onDecide}
        plannedSpeed={plannedSpeed}
      />
      <DaysTable days={analysis.days} canDecide={canDecide} onDecide={onDecide} />
      <ReviewPanel data={data} canDecide={canDecide} onReview={onReview} />
    </>
  );
}

function SeaTimeTable({
  goodWeather,
  whole,
}: {
  goodWeather: CpSeaTime | null;
  whole: CpSeaTime | null;
}) {
  const { t, k } = useCp();
  const n = useNumber();
  const one = { digits: 1, fixed: true } as const;
  // Artı kaybedilen, eksi kazanılan zaman; açıklama tablonun altındaki notta
  const diff = (value: CpSeaTime | null) => {
    if (!value || value.differenceHours === null) return '—';
    const lost = value.differenceHours > 0;
    return (
      <StatusBadge tone={lost ? 'warning' : 'success'} withIcon>
        {`${value.differenceHours > 0 ? '+' : ''}${n.unit(value.differenceHours, 'h', one)}`}
      </StatusBadge>
    );
  };
  return (
    <ScrollTable label={k('seaTimeTable')}>
      <Table className={[shared.table, classes.compare].join(' ')} withColumnBorders>
        <colgroup>
          <col className={classes.colParam} />
          <col className={classes.colValue} />
          <col className={classes.colValue} />
          <col className={classes.colValue} />
        </colgroup>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('voyage.overview.parameter')}</Table.Th>
            <Table.Th data-numeric>{k('contractualHours')}</Table.Th>
            <Table.Th data-numeric>{k('actualHours')}</Table.Th>
            <Table.Th>{t('voyage.overview.difference')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(
            [
              [k('goodWeatherShort'), goodWeather],
              [k('wholeShort'), whole],
            ] as const
          ).map(([label, value]) => (
            <Table.Tr key={label}>
              <Table.Td>{label}</Table.Td>
              <Table.Td data-numeric>{n.unit(value?.contractualHours ?? null, 'h', one)}</Table.Td>
              <Table.Td data-numeric>{n.unit(value?.actualHours ?? null, 'h', one)}</Table.Td>
              <Table.Td>{diff(value)}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <p className={classes.ruleNote}>{k('seaTimeNote')}</p>
    </ScrollTable>
  );
}

function CountedBadge({ included, byOffice }: { included: boolean; byOffice: boolean }) {
  const { k } = useCp();
  return (
    <>
      <StatusBadge tone={included ? 'success' : 'neutral'} withIcon={included}>
        {k(included ? 'included' : 'excludedBadge')}
      </StatusBadge>
      {byOffice ? <span className={cpClasses.sub}>{k('byOffice')}</span> : null}
    </>
  );
}

function LegTable({
  legs,
  canDecide,
  onDecide,
  plannedSpeed,
}: {
  legs: readonly CpLeg[];
  canDecide: boolean;
  onDecide: (target: CpDecisionTarget) => void;
  plannedSpeed: number | null;
}) {
  const { t, k, c } = useCp();
  const n = useNumber();
  const one = { digits: 1, fixed: true } as const;
  return (
    <Panel title={k('legs')} icon={<IconRoute size={20} />} divider>
      {legs.length === 0 ? (
        <EmptyState icon={<IconRoute size={22} stroke={1.8} />} title={k('noLegs')} />
      ) : (
        <ScrollTable label={k('legsTable')}>
          <table className={cpClasses.table}>
            <thead>
              <tr>
                <th scope="col">{k('legPeriod')}</th>
                <th scope="col">{k('relationHead')}</th>
                <th scope="col" data-numeric>
                  {c('performanceHours')}
                </th>
                <th scope="col" data-numeric>
                  {t('voyage.overview.distance')}
                </th>
                <th scope="col" data-numeric>
                  {t('voyage.performance.avgSpeed')}
                </th>
                <th scope="col">{k('counted')}</th>
              </tr>
            </thead>
            <tbody>
              {legs.map((leg) => (
                <tr key={leg.id}>
                  <td>
                    <span className={cpClasses.primary} style={{ fontWeight: 400 }}>
                      {formatDateTime(leg.start, 0)}
                      <span className={cpClasses.sub}>
                        {`→ ${leg.end ? formatDateTime(leg.end, 0) : c('open')}`}
                      </span>
                    </span>
                  </td>
                  <td>
                    <StatusBadge
                      tone={leg.relation === 'inside' ? 'info' : 'warning'}
                      variant="dot"
                    >
                      {k(`relation.${leg.relation}`)}
                    </StatusBadge>
                  </td>
                  <td data-numeric>{n(leg.hours, one)}</td>
                  <td data-numeric>{n(leg.distanceNm, one)}</td>
                  <td data-numeric>
                    {n(leg.avgSpeedKn, one)}
                    {leg.speedOk === false && plannedSpeed !== null ? (
                      <span className={cpClasses.sub}>{c('below')}</span>
                    ) : null}
                  </td>
                  <td>
                    <span className={cpClasses.rowActions} style={{ alignItems: 'center' }}>
                      <CountedBadge included={leg.included} byOffice={leg.decision !== null} />
                      {canDecide ? (
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          aria-label={k(leg.included ? 'excludeLeg' : 'includeLeg', {
                            at: formatDateTime(leg.start, 0),
                          })}
                          onClick={() =>
                            onDecide({
                              kind: 'leg',
                              id: leg.id,
                              action: leg.included ? 'exclude' : 'include',
                              at: leg.start,
                            })
                          }
                        >
                          {k(leg.included ? 'exclude' : 'include')}
                        </Button>
                      ) : null}
                    </span>
                    {leg.decision?.reason ? (
                      <span className={cpClasses.sub}>{leg.decision.reason}</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollTable>
      )}
    </Panel>
  );
}

const PAGE = 10;

/** Dahil bacaklardaki Noon raporları (G4-82, G4-84): iyi hava, hesaba girip girmediği, karar. */
function DaysTable({
  days,
  canDecide,
  onDecide,
}: {
  days: readonly CpAnalysisDay[];
  canDecide: boolean;
  onDecide: (target: CpDecisionTarget) => void;
}) {
  const { t, k, c } = useCp();
  const n = useNumber();
  const [ascending, setAscending] = useState(true);
  const [page, setPage] = useState(1);
  const one = { digits: 1, fixed: true } as const;
  const two = { digits: 2, fixed: true } as const;
  const hs = (v: number | null) =>
    v === null ? '—' : Number.isFinite(v) ? n(v, two) : `> ${n(DOUGLAS_WIND_SEA_M[8], 0)}`;
  const sorted = ascending ? [...days] : [...days].reverse();
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE));
  const shown = sorted.slice((page - 1) * PAGE, page * PAGE);
  const good = days.filter((d) => d.goodWeather === true && !d.excluded).length;
  const counted = (day: CpAnalysisDay) => !day.excluded && day.goodWeather === true;
  const weather = (day: CpAnalysisDay) =>
    day.excluded
      ? { tone: 'info' as const, text: c('excludedDay') }
      : day.goodWeather === null
        ? { tone: 'neutral' as const, text: c('unknown') }
        : day.goodWeather
          ? { tone: 'success' as const, text: c('yes') }
          : { tone: 'warning' as const, text: c('no') };
  return (
    <Panel
      title={c('days')}
      icon={<IconSunWind size={20} />}
      divider
      extra={
        <span className={classes.count}>
          {days.length > 0 ? c('goodDays', { good, total: days.length }) : null}
          <Tooltip label={c('weatherNote')} withArrow multiline w={320}>
            <span className={classes.infoIcon} role="img" aria-label={c('weatherNoteLabel')}>
              <IconInfoCircle size={16} aria-hidden />
            </span>
          </Tooltip>
        </span>
      }
    >
      {days.length === 0 ? (
        <EmptyState icon={<IconSunWind size={22} stroke={1.8} />} title={c('noDays')} />
      ) : (
        <>
          <ScrollTable label={c('daysTable')}>
            <Table
              className={[shared.table, classes.days].join(' ')}
              withColumnBorders
              // Dar ekranda tablo kendi kutusunda kayar; sütunlar üst üste binmez
              style={{ minWidth: 880 }}
            >
              <colgroup>
                <col className={classes.colWhen} />
                <col />
                <col />
                <col />
                <col className={classes.colCp} />
                <col />
                <col />
                <col />
                <col />
                <col className={classes.colBadge} />
              </colgroup>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th aria-sort={ascending ? 'ascending' : 'descending'}>
                    <UnstyledButton
                      className={classes.sort}
                      onClick={() => setAscending((v) => !v)}
                    >
                      {c('when')}
                      {ascending ? (
                        <IconSortAscending size={14} aria-hidden />
                      ) : (
                        <IconSortDescending size={14} aria-hidden />
                      )}
                    </UnstyledButton>
                  </Table.Th>
                  <Table.Th data-numeric>{t('voyage.overview.distance')}</Table.Th>
                  <Table.Th data-numeric>{c('hours')}</Table.Th>
                  <Table.Th data-numeric>{t('voyage.performance.avgSpeed')}</Table.Th>
                  <Table.Th>{c('vsCp')}</Table.Th>
                  <Table.Th data-numeric>{c('beaufort')}</Table.Th>
                  <Table.Th data-numeric>{c('seaState')}</Table.Th>
                  <Table.Th data-numeric>{c('swellHeight')}</Table.Th>
                  <Table.Th data-numeric>{c('waveHeight')}</Table.Th>
                  <Table.Th>{c('goodWeather')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {shown.map((day) => {
                  const w = weather(day);
                  return (
                    <Table.Tr key={day.id}>
                      <Table.Td>{formatDateTime(day.reportedAt, 0)}</Table.Td>
                      <Table.Td data-numeric>{n(day.distanceNm, one)}</Table.Td>
                      <Table.Td data-numeric>{n(day.hours, one)}</Table.Td>
                      <Table.Td data-numeric>{n(day.avgSpeedKn, one)}</Table.Td>
                      <Table.Td>
                        {day.speedOk === null ? (
                          '—'
                        ) : (
                          <StatusBadge tone={day.speedOk ? 'success' : 'warning'} withIcon>
                            {day.speedOk ? t('voyage.overview.ok') : c('below')}
                          </StatusBadge>
                        )}
                      </Table.Td>
                      <Table.Td data-numeric>{day.beaufort ?? '—'}</Table.Td>
                      <Table.Td data-numeric>{day.seaState ?? '—'}</Table.Td>
                      <Table.Td data-numeric>{n(day.swellHeightM, one)}</Table.Td>
                      <Table.Td data-numeric>{hs(day.waveHeightM)}</Table.Td>
                      <Table.Td style={{ whiteSpace: 'normal' }}>
                        <StatusBadge tone={w.tone} withIcon={w.tone !== 'neutral'}>
                          {w.text}
                        </StatusBadge>
                        {day.exclusion ? (
                          <span className={cpClasses.sub}>
                            {k(`dayExclusion.${day.exclusion}`)}
                          </span>
                        ) : day.officeIncluded ? (
                          <span className={cpClasses.sub}>{k('officeIncluded')}</span>
                        ) : null}
                        {canDecide ? (
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            px={0}
                            aria-label={k(counted(day) ? 'excludeDay' : 'includeDay', {
                              at: formatDateTime(day.reportedAt, 0),
                            })}
                            onClick={() =>
                              onDecide({
                                kind: 'day',
                                id: day.id,
                                action: counted(day) ? 'exclude' : 'include',
                                at: day.reportedAt,
                              })
                            }
                          >
                            {k(counted(day) ? 'exclude' : 'include')}
                          </Button>
                        ) : null}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollTable>
          {pages > 1 ? (
            <Pagination
              total={pages}
              value={page}
              onChange={setPage}
              size="sm"
              className={classes.pagination}
              aria-label={c('pagination')}
              getControlProps={(control) => ({ 'aria-label': c(`page.${control}`) })}
              getItemProps={(item) => ({ 'aria-label': c('page.number', { page: item }) })}
            />
          ) : null}
        </>
      )}
    </Panel>
  );
}

function ReviewPanel({
  data,
  canDecide,
  onReview,
}: {
  data: CharterPartyAnalysisData;
  canDecide: boolean;
  onReview: () => void;
}) {
  const { k } = useCp();
  const target = (event: CharterPartyEventView) =>
    event.targetKind
      ? k(`targets.${event.targetKind}`, {
          at: formatDateTime(
            data.analysis.days.find((d) => d.id === event.targetId)?.reportedAt ??
              data.analysis.legs.find((l) => l.id === event.targetId)?.start ??
              event.createdAt,
            0,
          ),
        })
      : null;
  const events = [...data.events].reverse();
  return (
    <Panel
      title={k('review')}
      icon={<IconChecklist size={20} />}
      divider
      extra={
        canDecide && data.result.label !== 'reviewed' ? (
          <Button size="xs" onClick={onReview}>
            {k('markReviewed')}
          </Button>
        ) : null
      }
    >
      {!canDecide ? <p className={cpClasses.hint}>{k('shipNote')}</p> : null}
      {events.length === 0 ? (
        <p className={cpClasses.hint}>{k('noTrail')}</p>
      ) : (
        <ScrollTable label={k('reviewTrail')}>
          <table className={cpClasses.table} style={{ minWidth: 640 }}>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <td>
                    <span className={cpClasses.primary}>
                      {k(`actions.${event.action}`)}
                      {target(event) ? (
                        <span className={cpClasses.sub}>{target(event)}</span>
                      ) : null}
                    </span>
                  </td>
                  <td>
                    {event.reason ?? '—'}
                    {event.reference ? (
                      <span className={cpClasses.sub}>{event.reference}</span>
                    ) : null}
                  </td>
                  <td>
                    {k('trailBy', {
                      name: event.createdByName ?? '—',
                      at: formatDateTime(event.createdAt, 0),
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollTable>
      )}
    </Panel>
  );
}

/**
 * Voyage ▸ Performance & Analysis ▸ Charter Party Performance (ADR 0084 Ek). Gemi (web) ve CP
 * seçilir (`?vessel=&cp=`); ofiste gün ve bacak gerekçeyle hariç tutulur ya da dahil edilir,
 * analiz incelenir.
 */
export function CharterPartyPerformanceScreen({ source }: { source: VoyageSource }) {
  const shell = useShell();
  const { t, k } = useCp();
  const params = new URLSearchParams(shell?.search ?? '');
  const [vesselId, setVesselId] = useState<string | null>(() => params.get('vessel'));
  const [cpId, setCpId] = useState<string | null>(() => params.get('cp'));
  const [load, setLoad] = useState<CpAnalysisLoad>({ kind: 'loading' });
  const [message, setMessage] = useState<Message>(null);
  const [deciding, setDeciding] = useState<CpDecisionTarget | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const editor = source.charterPartyEditor;
  const errorText = (error: unknown) =>
    t(`voyage.charterParties.errors.${errorCode(error)}`, {
      defaultValue: t('voyage.charterParties.errors.GENERIC'),
    });

  const read = useCallback(async () => {
    setLoad({ kind: 'loading' });
    try {
      const vessels = await source.vessels();
      const vessel = vessels.find((v) => v.id === vesselId) ?? vessels[0] ?? null;
      const register: CharterPartyRegisterData =
        vessel && source.charterParties
          ? await source.charterParties(vessel.id)
          : { items: [], passages: [], hireReports: [] };
      const item: CharterPartyItem | null =
        register.items.find((i) => i.id === cpId) ?? register.items[0] ?? null;
      const data =
        item && source.charterPartyAnalysis ? await source.charterPartyAnalysis(item.id) : null;
      setLoad({ kind: 'ready', vessels, vessel, register, cpId: item?.id ?? null, data });
    } catch (error) {
      setLoad({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId, cpId]);
  useEffect(() => {
    void read();
  }, [read]);

  const ready = load.kind === 'ready' ? load : null;
  const canDecide = !!editor && (ready?.data?.can?.decide ?? false);
  const close = () => {
    setDeciding(null);
    setReviewing(false);
    setReason('');
    setReference('');
  };
  const run = async (work: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await work();
      setMessage({ tone: 'success', text: k(done) });
      close();
      await read();
    } catch (error) {
      setMessage({ tone: 'danger', text: errorText(error) });
      close();
    } finally {
      setBusy(false);
    }
  };
  const decide = () => {
    const data = ready?.data;
    if (!editor || !deciding || !data) return;
    void run(
      () =>
        editor.decide(data.charterParty.vesselId, {
          charterPartyId: data.charterParty.id,
          action: deciding.action,
          targetKind: deciding.kind,
          targetId: deciding.id,
          reason: reason.trim(),
          reference: reference.trim() || null,
        }),
      'decided',
    );
  };
  const review = () => {
    const data = ready?.data;
    if (!editor || !data) return;
    void run(
      () => editor.review(data.charterParty.id, data.analysisHash, reason.trim() || null),
      'reviewed',
    );
  };

  const decisionTitle = deciding
    ? k(`${deciding.action}${deciding.kind === 'leg' ? 'Leg' : 'Day'}`, {
        at: formatDateTime(deciding.at, 0),
      })
    : '';

  return (
    <>
      <CharterPartyPerformanceView
        load={load}
        scope={source.scope}
        canDecide={canDecide}
        message={message}
        onVessel={(id) => {
          setMessage(null);
          setCpId(null);
          setVesselId(id);
        }}
        onCp={(id) => {
          setMessage(null);
          setCpId(id);
        }}
        onRetry={() => void read()}
        onOpenRegister={() => shell?.navigate('/voyage/charter-parties')}
        onDecide={(target) => {
          setMessage(null);
          setDeciding(target);
        }}
        onReview={() => {
          setMessage(null);
          setReviewing(true);
        }}
      />
      <Modal
        opened={deciding !== null}
        onClose={close}
        title={decisionTitle}
        closeButtonProps={{ 'aria-label': k('cancel') }}
      >
        <div className={cpClasses.form}>
          <Textarea
            label={k('decisionReason')}
            description={k('decisionHint')}
            required
            maxLength={1000}
            autosize
            minRows={2}
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
          />
          <TextInput
            label={k('decisionReference')}
            description={k('decisionReferenceHint')}
            maxLength={1000}
            value={reference}
            onChange={(event) => setReference(event.currentTarget.value)}
          />
        </div>
        <div className={cpClasses.dialogActions}>
          <Button variant="default" onClick={close}>
            {k('cancel')}
          </Button>
          <Button loading={busy} disabled={!reason.trim()} onClick={decide}>
            {deciding ? k(deciding.action) : ''}
          </Button>
        </div>
      </Modal>
      <Modal
        opened={reviewing}
        onClose={close}
        title={k('markReviewed')}
        closeButtonProps={{ 'aria-label': k('cancel') }}
      >
        <p className={cpClasses.hint}>{k('claims')}</p>
        <Textarea
          label={k('reviewNote')}
          maxLength={1000}
          autosize
          minRows={2}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
        />
        <div className={cpClasses.dialogActions}>
          <Button variant="default" onClick={close}>
            {k('cancel')}
          </Button>
          <Button loading={busy} onClick={review}>
            {k('markReviewed')}
          </Button>
        </div>
      </Modal>
    </>
  );
}
